/**
 * Deep-links from a mark into the triage tray (spec §6.2 / §6.3 / §6.4).
 * `undefined` when there is no id to scope by — the chip then renders without
 * a link rather than pointing at an unscoped tray.
 *
 * The param NAMES deviate from the spec's §6.2/§6.4 (`pairId` / `ruleId` /
 * `groupId`): those are already taken by the notifications feed on the same
 * page and the same URL — see `TRIAGE_SCOPE_PARAM`.
 */

const TRIAGE_TRAY = '/predictions/notifications';

/**
 * `byGroupId`'s key for everything that could not be placed in a group (spec
 * §4.7). JSON has no null key, so the wire carries the string — and so does the
 * deep link the `ungrouped / unresolved` entry points at.
 */
export const TRIAGE_UNGROUPED_KEY = 'null';

/** What that bucket is called on screen. */
export const TRIAGE_UNGROUPED_LABEL = 'ungrouped / unresolved';

function trayLink(kind: TriageScopeKind, id: string): string {
  const params = new URLSearchParams();
  params.set('tab', 'triage');
  params.set(TRIAGE_SCOPE_PARAM[kind], id);
  return `${TRIAGE_TRAY}?${params.toString()}`;
}

export function triagePairDeepLink(
  pairId: string | null | undefined
): string | undefined {
  return pairId ? trayLink('pairId', pairId) : undefined;
}

export function triageGroupDeepLink(
  groupId: string | null | undefined
): string | undefined {
  return groupId ? trayLink('groupId', groupId) : undefined;
}

export function triageRuleDeepLink(
  ruleId: string | null | undefined
): string | undefined {
  return ruleId ? trayLink('ruleId', ruleId) : undefined;
}

/* ── The reading half ─────────────────────────────────────────────────── */

export type TriageScopeKind = 'pairId' | 'ruleId' | 'groupId';

/**
 * 🚨 The URL keys are `tri`-PREFIXED, and that prefix is load-bearing twice
 * over. Bare `pairId` / `ruleId` / `groupId` are the notifications FEED's own
 * filter keys (`readFilterFromParams`), so a tray scope written bare was read
 * by the feed as a filter the operator never set — and the fix for THAT was to
 * strip the keys when leaving the tray, which threw away the filter an
 * operator HAD set on the feed before opening the tray.
 *
 * Prefixed, the two vocabularies cannot touch: the feed's URL writer already
 * carries every `tri*` key across a tab change on purpose, so the tray's scope
 * survives a trip to the feed and back, and the feed's filter survives a trip
 * to the tray and back. Nothing needs clearing on the way out.
 */
export const TRIAGE_SCOPE_PARAM: Record<TriageScopeKind, string> = {
  pairId: 'triPairId',
  ruleId: 'triRuleId',
  groupId: 'triGroupId'
};

/**
 * Read in this order, so a URL carrying more than one is resolved the same way
 * every time. `trayLink` only ever writes one.
 */
export const TRIAGE_SCOPE_PARAMS: readonly TriageScopeKind[] = [
  'pairId',
  'ruleId',
  'groupId'
];

export interface TriageScope {
  kind: TriageScopeKind;
  id: string;
}

export function readTriageScope(params: URLSearchParams): TriageScope | null {
  for (const kind of TRIAGE_SCOPE_PARAMS) {
    const id = params.get(TRIAGE_SCOPE_PARAM[kind]);
    if (id) return { kind, id };
  }
  return null;
}

/**
 * A copy of `params` with every scope key removed — what `Show all cards`
 * clears. It touches ONLY the `tri*` keys, so the feed's own filter is not
 * collateral.
 */
export function withoutTriageScope(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const kind of TRIAGE_SCOPE_PARAMS) next.delete(TRIAGE_SCOPE_PARAM[kind]);
  return next;
}

export const TRIAGE_SCOPE_NOUN: Record<TriageScopeKind, string> = {
  pairId: 'pair',
  ruleId: 'rule',
  groupId: 'group'
};

/**
 * The cards a scope admits. No scope ⇒ the list UNCHANGED.
 *
 * All three kinds filter. The group arm was the exception until the endpoint
 * started resolving each card's opener group at read time: a `groupId`
 * deep-link used to render the WHOLE tray under a banner admitting it was not
 * filtered, so the one link an operator follows from a group badge landed them
 * on every open card in the fleet.
 *
 * The group arm reads the server's own attribution (`group.id`) and NEVER
 * re-derives one from the rule or the pair — the server refuses to place a
 * card it cannot place on exactly one group, and re-deriving here would undo
 * that refusal.
 */
export function applyTriageScope<
  T extends {
    pairId?: string | null;
    ruleId?: string | null;
    group?: { id?: string | null } | null;
  }
>(items: T[], scope: TriageScope | null): T[] {
  if (scope === null) return items;
  if (scope.kind === 'pairId') {
    return items.filter(item => item.pairId === scope.id);
  }
  if (scope.kind === 'ruleId') {
    return items.filter(item => item.ruleId === scope.id);
  }
  return items.filter(item => {
    const groupId = item.group?.id ?? null;
    return scope.id === TRIAGE_UNGROUPED_KEY
      ? groupId === null
      : groupId === scope.id;
  });
}
